import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Check, Zap } from "lucide-react";
import { cn } from "@/lib/utils";

const PLANS = [
  {
    id: "monthly",
    priceId: "price_1TEaqdQuqvR3KBZaK45aRBko",
    mode: "subscription" as const,
    label: "Monthly",
    price: "$29",
    period: "/mo",
    description: "Billed monthly, cancel anytime",
  },
  {
    id: "annual",
    priceId: "price_1TEarIQuqvR3KBZaGubk4vFZ",
    mode: "subscription" as const,
    label: "Annual",
    price: "$290",
    period: "/yr",
    description: "Save ~17% vs monthly",
    badge: "Best value",
  },
  {
    id: "lifetime",
    priceId: "price_1TEarIQuqvR3KBZaxZwK30Mq",
    mode: "payment" as const,
    label: "Lifetime",
    price: "$199",
    period: "",
    description: "Pay once, yours forever",
    badge: "Limited deal",
    limited: true,
  },
];

interface UpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (priceId: string, mode: "subscription" | "payment") => void;
  loading: boolean;
}

export function UpgradeModal({ open, onOpenChange, onSelect, loading }: UpgradeModalProps) {
  const [selected, setSelected] = useState("annual");

  const selectedPlan = PLANS.find((p) => p.id === selected)!;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Upgrade to Pro
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-3 py-4">
          {PLANS.map((plan) => (
            <button
              key={plan.id}
              onClick={() => setSelected(plan.id)}
              className={cn(
                "relative flex items-center justify-between rounded-lg border p-4 text-left transition-colors",
                selected === plan.id
                  ? "border-primary bg-primary/5 ring-1 ring-primary"
                  : "border-border hover:border-muted-foreground/30"
              )}
            >
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    "flex h-5 w-5 items-center justify-center rounded-full border-2 transition-colors",
                    selected === plan.id ? "border-primary bg-primary" : "border-muted-foreground/30"
                  )}
                >
                  {selected === plan.id && <Check className="h-3 w-3 text-primary-foreground" />}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">{plan.label}</span>
                    {plan.badge && (
                      <Badge
                        variant={plan.limited ? "destructive" : "default"}
                        className="text-[10px] px-1.5 py-0"
                      >
                        {plan.limited && <Zap className="h-2.5 w-2.5 mr-0.5" />}
                        {plan.badge}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{plan.description}</p>
                </div>
              </div>
              <div className="text-right">
                <span className="text-sm font-semibold text-foreground">{plan.price}</span>
                <span className="text-xs text-muted-foreground">{plan.period}</span>
              </div>
            </button>
          ))}
        </div>

        <Button
          className="w-full"
          onClick={() => onSelect(selectedPlan.priceId, selectedPlan.mode)}
          disabled={loading}
        >
          {loading ? "Redirecting…" : `Continue with ${selectedPlan.label}`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
